import type { OrgLlmModel } from "@/shared/domain/orgs";
import { isRobotoManaged } from "@/shared/domain/orgs/orgModelConfig";
import { MoreVertical } from "@/shared/foundation/icons";
import { DropdownMenu, RobotoButton } from "@/shared/foundation/primitives";

import {
  isEnabled,
  modelListError,
  withDefaultModel,
  withoutModel,
  withUtilityModel,
} from "./modelList";
import { VerifyModelAction } from "./ModelVerification";
import styles from "./OrgAiModels.module.css";

interface ModelRowActionsProps {
  orgId: string;
  model: OrgLlmModel;
  /** The saved list the quick actions change. */
  models: readonly OrgLlmModel[];
  /** Own models can be verified only on a premium org with own connections enabled. */
  canVerify: boolean;
  /** Own models can be edited only while the org's own connections are enabled. */
  canEditOwn: boolean;
  /**
   * Whether the menu offers the utility role (the own-connections flag). Off,
   * it offers neither making the model the utility model nor unsetting it.
   */
  offersUtility: boolean;
  /**
   * A save is in flight. The menu still opens, so nothing looks disabled, but
   * its actions are ignored until the save settles, so no second save queues.
   */
  busy: boolean;
  onEdit: (model: OrgLlmModel) => void;
  /** Saves the list with one quick action applied. */
  onSave: (next: OrgLlmModel[]) => void;
  /** Asks to remove the model; the caller confirms first. */
  onRemove: (model: OrgLlmModel) => void;
}

/**
 * An admin's actions on one row: Verify and Edit for an own model (when the
 * org may verify and edit them), and a menu of one-click changes (default,
 * utility when offered, chat picker) and Remove. A change that would leave
 * the list invalid is offered disabled. A switched-off model offers only
 * Remove, plus Edit when it is an own model the org may edit.
 */
export function ModelRowActions({
  orgId,
  model,
  models,
  canVerify,
  canEditOwn,
  offersUtility,
  busy,
  onEdit,
  onSave,
  onRemove,
}: ModelRowActionsProps) {
  const own = !isRobotoManaged(model);
  const enabled = isEnabled(model);
  const inPicker = model.in_picker !== false;
  const isUtility = model.is_utility === true;
  const save = (next: OrgLlmModel[]) => {
    if (!busy) {
      onSave(next);
    }
  };
  const remove = (target: OrgLlmModel) => {
    if (!busy) {
      onRemove(target);
    }
  };
  const pickerToggled = models.map((m) =>
    m.id === model.id ? { ...m, in_picker: !inPicker } : m,
  );

  return (
    <span className={styles.rowActions}>
      {own && enabled && canVerify && (
        <VerifyModelAction orgId={orgId} model={model} />
      )}
      {own && canEditOwn && (
        <RobotoButton
          variant="ghost"
          size="sm"
          onClick={() => onEdit(model)}
          aria-label={`Edit ${model.label}`}
        >
          Edit
        </RobotoButton>
      )}
      <DropdownMenu>
        <DropdownMenu.Trigger
          render={
            <RobotoButton
              variant="ghost"
              size="sm"
              icon={<MoreVertical />}
              aria-label={`More actions for ${model.label}`}
              aria-disabled={busy || undefined}
            />
          }
        />
        <DropdownMenu.Content align="end">
          {enabled && (
            <>
              {inPicker && !model.is_default && (
                <DropdownMenu.Item
                  onClick={() => save(withDefaultModel(models, model.id))}
                >
                  Make default
                </DropdownMenu.Item>
              )}
              {offersUtility && (
                <DropdownMenu.Item
                  onClick={() =>
                    save(withUtilityModel(models, isUtility ? null : model.id))
                  }
                >
                  {isUtility
                    ? "Use Roboto's utility model"
                    : "Make utility model"}
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item
                disabled={modelListError(pickerToggled) !== null}
                onClick={() => save(pickerToggled)}
              >
                {inPicker ? "Hide from chat picker" : "Show in chat picker"}
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
            </>
          )}
          <DropdownMenu.Item
            variant="danger"
            disabled={modelListError(withoutModel(models, model.id)) !== null}
            onClick={() => remove(model)}
          >
            Remove
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu>
    </span>
  );
}
